import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Grid3X3, 
  Smartphone, 
  Headphones, 
  Home, 
  Shield, 
  Briefcase,
  Sparkles,
  ThumbsUp,
  Percent,
  ChevronDown,
  ChevronUp,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';

const iconMap = {
  'grid': Grid3X3,
  'smartphone': Smartphone,
  'headphones': Headphones,
  'home': Home,
  'shield': Shield,
  'briefcase': Briefcase,
  'sparkles': Sparkles,
  'thumbs-up': ThumbsUp,
  'percent': Percent
};

const Sidebar = ({ 
  categories, 
  filters, 
  priceRanges,
  selectedCategory, 
  selectedPriceRange,
  selectedFilters,
  onCategoryChange, 
  onPriceRangeChange,
  onFilterChange,
  isMobileOpen,
  onMobileClose
}) => {
  const [isCategoryExpanded, setIsCategoryExpanded] = React.useState(true);
  const [isFiltersExpanded, setIsFiltersExpanded] = React.useState(true);
  const [isPriceExpanded, setIsPriceExpanded] = React.useState(true);

  const SidebarContent = () => (
    <div className="space-y-6">
      {/* Category Section */}
      <div>
        <button 
          className="w-full flex items-center justify-between text-left mb-4"
          onClick={() => setIsCategoryExpanded(!isCategoryExpanded)}
        >
          <h3 className="font-display font-semibold text-foreground text-lg">Category</h3>
          {isCategoryExpanded ? (
            <ChevronUp className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          )}
        </button>
        
        <AnimatePresence>
          {isCategoryExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <RadioGroup value={selectedCategory} onValueChange={onCategoryChange}>
                <div className="space-y-2">
                  {categories.map((category) => {
                    const Icon = iconMap[category.icon] || Grid3X3;
                    const isSelected = selectedCategory === category.id;
                    
                    return (
                      <motion.div
                        key={category.id}
                        whileHover={{ x: 4 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Label
                          htmlFor={category.id}
                          className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                            isSelected 
                              ? 'bg-primary text-primary-foreground' 
                              : 'hover:bg-muted'
                          }`}
                        >
                          <RadioGroupItem
                            value={category.id}
                            id={category.id}
                            className="sr-only"
                          />
                          <Icon className={`w-4 h-4 ${isSelected ? 'text-primary-foreground' : 'text-muted-foreground'}`} />
                          <span className={`flex-1 text-sm font-medium ${isSelected ? 'text-primary-foreground' : 'text-foreground'}`}>
                            {category.name}
                          </span>
                          {category.id === 'all' && (
                            <Badge 
                              className={`text-xs px-2 py-0.5 rounded-full ${
                                isSelected 
                                  ? 'bg-primary-foreground/20 text-primary-foreground' 
                                  : 'bg-destructive text-destructive-foreground'
                              }`}
                            >
                              {category.count}
                            </Badge>
                          )}
                        </Label>
                      </motion.div>
                    );
                  })}
                </div>
              </RadioGroup>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Separator />

      {/* Filters Section */}
      <div>
        <button 
          className="w-full flex items-center justify-between text-left mb-4"
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
        >
          <h3 className="font-display font-semibold text-foreground text-lg">Sotingory Finwords</h3>
          {isFiltersExpanded ? (
            <ChevronUp className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          )}
        </button>
        
        <AnimatePresence>
          {isFiltersExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="space-y-2">
                {filters.map((filter) => {
                  const Icon = iconMap[filter.icon] || Sparkles;
                  const isSelected = selectedFilters.includes(filter.id);
                  
                  return (
                    <motion.div
                      key={filter.id}
                      whileHover={{ x: 4 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Label
                        htmlFor={`filter-${filter.id}`}
                        className="flex items-center gap-3 p-3 rounded-xl cursor-pointer hover:bg-muted transition-colors"
                      >
                        <Checkbox
                          id={`filter-${filter.id}`}
                          checked={isSelected}
                          onCheckedChange={() => onFilterChange(filter.id)}
                          className="rounded-full"
                        />
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">
                          {filter.name}
                        </span>
                      </Label>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <Separator />

      {/* Price Range Section */}
      <div>
        <button 
          className="w-full flex items-center justify-between text-left mb-4"
          onClick={() => setIsPriceExpanded(!isPriceExpanded)}
        >
          <h3 className="font-display font-semibold text-foreground text-lg">Price Range</h3>
          {isPriceExpanded ? (
            <ChevronUp className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-5 h-5 text-muted-foreground" />
          )}
        </button>
        
        <AnimatePresence>
          {isPriceExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <RadioGroup value={selectedPriceRange} onValueChange={onPriceRangeChange}>
                <div className="space-y-2">
                  {priceRanges.map((range) => {
                    const isSelected = selectedPriceRange === range.id;
                    
                    return (
                      <motion.div
                        key={range.id}
                        whileHover={{ x: 4 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Label
                          htmlFor={`price-${range.id}`}
                          className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                            isSelected 
                              ? 'bg-muted' 
                              : 'hover:bg-muted/50'
                          }`}
                        >
                          <RadioGroupItem
                            value={range.id}
                            id={`price-${range.id}`}
                            className="border-muted-foreground"
                          />
                          <span className="text-sm font-medium text-foreground">
                            {range.label}
                          </span>
                        </Label>
                      </motion.div>
                    );
                  })}
                </div>
              </RadioGroup>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden md:block w-64 lg:w-72 flex-shrink-0">
        <div className="sticky top-24 p-4 lg:p-6 bg-card rounded-3xl shadow-soft">
          <ScrollArea className="h-[calc(100vh-140px)]">
            <SidebarContent />
          </ScrollArea>
        </div>
      </aside>

      {/* Mobile Sidebar Drawer */}
      <AnimatePresence>
        {isMobileOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-40 md:hidden"
              onClick={onMobileClose}
            />
            
            {/* Drawer */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-card z-50 md:hidden shadow-xl"
            >
              <div className="flex items-center justify-between p-4 border-b border-border">
                <h2 className="font-display font-semibold text-lg">Filters</h2>
                <Button variant="ghost" size="icon" onClick={onMobileClose}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <ScrollArea className="h-[calc(100vh-65px)] p-4">
                <SidebarContent />
              </ScrollArea>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
